import { useState } from 'react';

import { cx } from '@/utils/cx';

import type { NavItem } from '../header-navigation';

import { NavItemBase } from './nav-item';

interface NavListProps {
  /** URL of the currently active item. */
  activeUrl?: string;
  /** Additional CSS classes to apply to the list. */
  className?: string;
  /** List of items to display. */
  items: NavItem[];
}

export const NavList = ({ activeUrl, items, className }: NavListProps) => {
  const [open, setOpen] = useState(false);

  const activeItem = items
    .flatMap((item) => ('divider' in item ? [] : [item]))
    .find(
      (item) =>
        item.href === activeUrl ||
        item.items
          ?.flatMap((subItem) => ('divider' in subItem ? [] : [subItem]))
          .some((subItem) => subItem.href === activeUrl),
    );
  const [currentItem, setCurrentItem] = useState(activeItem);

  return (
    <ul className={cx('mt-4 flex flex-col px-2 lg:px-4', className)}>
      {items.map((item, index) => {
        if ('divider' in item) {
          return (
            // Disable allowed because it's just a divider and we don't have
            // anything to key it by. Also they are fungible, so if react re-uses
            // the "wrong" one, it ends up exactly the same.
            // oxlint-disable-next-line react/no-array-index-key
            <li key={`divider-${index}`} className="w-full px-0.5 py-2">
              <hr className="h-px w-full border-none bg-border-secondary" />
            </li>
          );
        }

        if (item.items?.length) {
          return (
            <details
              key={item.label}
              open={activeItem?.href === item.href}
              className="appearance-none py-0.5"
              onToggle={(e) => {
                setOpen(e.currentTarget.open);
                setCurrentItem(item);
              }}
            >
              <NavItemBase
                href={item.href}
                badge={item.badge}
                icon={item.icon}
                type="collapsible"
              >
                {item.label}
              </NavItemBase>

              <dd>
                <ul className="py-0.5">
                  {item.items.map((childItem, childIndex) => {
                    if ('divider' in childItem) {
                      return (
                        // A divider has no label to key by, and dividers are
                        // fungible, so a positional key is fine here. Keying on
                        // the (undefined) label instead produces a null/duplicate
                        // key and remounts the list on every render.
                        // oxlint-disable-next-line react/no-array-index-key
                        <li key={`divider-${childIndex}`} className="py-0.5">
                          <hr className="h-px w-full border-none bg-border-secondary" />
                        </li>
                      );
                    }
                    return (
                      <li
                        key={childItem.label}
                        className="py-0.5"
                        data-testid={childItem.testId}
                      >
                        <NavItemBase
                          href={childItem.href}
                          badge={childItem.badge}
                          type="collapsible-child"
                          current={activeUrl === childItem.href}
                        >
                          {childItem.label}
                        </NavItemBase>
                      </li>
                    );
                  })}
                </ul>
              </dd>
            </details>
          );
        }

        return (
          <li key={item.label} className="py-0.5" data-testid={item.testId}>
            <NavItemBase
              type="link"
              badge={item.badge}
              icon={item.icon}
              href={item.href}
              current={currentItem?.href === item.href}
              open={open && currentItem?.href === item.href}
            >
              {item.label}
            </NavItemBase>
          </li>
        );
      })}
    </ul>
  );
};
